import React, { ReactNode, useState, useEffect, useRef } from 'react';
import { Box, TextField, Typography, IconButton, Paper } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import UndoIcon from '@mui/icons-material/Undo';
import debounce from 'debounce';
import DropDown from '../../baseComponents/dropdown';
import Field from '../../../interfaces/field';
import { FieldAliasRow } from '../types/fieldAliasTypes';
import AddFieldModal from '../../modals/addFieldModal';
import { textPattern } from '../../../constants/patterns';
import FieldService from '../../../services/fieldService';
import store from '../../../store/store';

interface BaseFieldRowProps {
    row: FieldAliasRow;
    index: number;
    updateRow: (id: string, field: keyof FieldAliasRow, value: any) => void;
    removeRow: (id: string) => void;
    undoDelete: (id: string) => void;
    isDuplicateAliasName: (aliasName: string, currentId: string) => boolean;
    secondaryFields: Field[];
    handleFieldSelection: (rowId: string, selectedFieldId: string) => void;
    stateType: 'full' | 'aliasOnly';
    children?: ReactNode;
}

const BaseFieldRow: React.FC<BaseFieldRowProps> = ({
    row,
    index,
    updateRow,
    removeRow,
    undoDelete,
    isDuplicateAliasName,
    secondaryFields,
    handleFieldSelection,
    stateType,
    children
}) => {
    // Local state management
    const [localRow, setLocalRow] = useState<FieldAliasRow>(row);
    const [localAliasName, setLocalAliasName] = useState(row.alias_name);
    const [addFieldModalOpen, setAddFieldModalOpen] = useState(false);

    // Create debounced update function
    const debouncedUpdateRef = useRef<ReturnType<typeof debounce>>();

    // Sync parent changes to local state
    useEffect(() => {
        setLocalRow(row);
        setLocalAliasName(row.alias_name);
    }, [row]);

    // Create debounced update function with cleanup
    useEffect(() => {
        debouncedUpdateRef.current = debounce((field: keyof FieldAliasRow, value: any) => {
            updateRow(row.id, field, value);
        }, 300);

        return () => {
            debouncedUpdateRef.current?.clear();
        };
    }, [updateRow, row.id]);

    // Handle text field changes
    const handleTextChange = (field: keyof FieldAliasRow, value: string) => {
        const cleanValue = value.replace(textPattern, "");

        if (stateType === 'full') {
            setLocalRow(prev => ({ ...prev, [field]: cleanValue }));
        } else if (field === 'alias_name') {
            setLocalAliasName(cleanValue);
        }
        // For aliasOnly state, we still need to update field_label and field_description in localRow
        else if (field === 'field_label' || field === 'field_description') {
            setLocalRow(prev => ({ ...prev, [field]: cleanValue }));
        }

        debouncedUpdateRef.current?.(field, cleanValue);
    };

    // Handle field creation callback - auto-select newly created field in this row
    const handleFieldCreated = async (newFieldId: string) => {
        try {
            setTimeout(async () => {
                await FieldService.updateFieldsInRedux();

                setTimeout(() => {
                    const currentAllFields = store.getState().fields.fields;
                    const newField = currentAllFields.find(field => field.field_id === newFieldId);

                    if (newField) {
                        handleFieldSelection(row.id, newFieldId);
                    }
                }, 100);
            }, 500);
        } catch (error) {
            console.error('Error updating fields after creation:', error);
        }
    };

    // Validate field alias
    const currentAliasName = stateType === 'full' ? localRow.alias_name : localAliasName;
    const isDuplicate = isDuplicateAliasName(currentAliasName, row.id);
    const hasEmptyAlias = !currentAliasName.trim();
    const validation = {
        isDuplicate,
        hasEmptyAlias,
        isValid: !isDuplicate && !hasEmptyAlias,
        errorMessage: hasEmptyAlias ? 'Alias name is required' :
            isDuplicate ? 'Duplicate alias name detected' : ''
    };

    return (
        <Paper
            elevation={1}
            sx={{
                p: 1.5,
                mb: 0.5,
                opacity: !row.is_active ? 0.6 : 1,
                backgroundColor: !row.is_active ? '#f5f5f5' : '#e3f2fd',
                border: '1px solid #e0e0e0',
                position: 'relative'
            }}
        >
            {/* Deleted overlay */}
            {!row.is_active && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 60,
                        bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1,
                        borderRadius: 1,
                        pointerEvents: 'none'
                    }}
                >
                    <Typography variant="h6" color="error" sx={{ fontWeight: 'bold' }}>
            MARKED FOR DELETION
                    </Typography>
                </Box>
            )}

            <Box display="flex" alignItems="center" gap={2}>
                <Box display="flex" gap={2} flex={1} mr={8}>
                    <Box width="20%">
                        <Typography variant="subtitle1">
              Field Alias #{index + 1}
                        </Typography>
                    </Box>

                    <Box width="40%">
                        <TextField
                            label="Alias Name"
                            value={currentAliasName}
                            onChange={(e) => handleTextChange('alias_name', e.target.value)}
                            fullWidth
                            size="small"
                            required
                            disabled={!row.is_active}
                            error={validation.isDuplicate || validation.hasEmptyAlias}
                            helperText={validation.errorMessage}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '&.Mui-error': {
                                        '& fieldset': {
                                            borderColor: '#f44336',
                                        },
                                    },
                                },
                            }}
                        />
                    </Box>

                    <Box width="40%" display="flex" gap={1}>
                        <Box flex={1} mt={-2}>
                            <DropDown
                                label='Map to existing field'
                                items={[
                                    { label: "🔗 Customer Reference", value: "CUSTOMER_REFERENCE" },
                                    { label: "➕ Create New Field", value: "CREATE_NEW_FIELD" },
                                    ...secondaryFields.map(field => ({
                                        label: `🎯 ${field.field_label || field.field_name}`,
                                        value: field.field_id
                                    }))
                                ]}
                                valueProp="value"
                                displayProp="label"
                                selectedValue={
                                    (row.customer_detail_field_id && "CUSTOMER_REFERENCE") ||
                                    (row.field_id && secondaryFields.find(field => field.field_id === row.field_id) ? row.field_id : "") ||
                                    ""
                                }
                                onSelection={(value) => {
                                    if (value === "CREATE_NEW_FIELD") {
                                        setAddFieldModalOpen(true);
                                    } else {
                                        handleFieldSelection(row.id, value);
                                    }
                                }}
                                disabled={!row.is_active}
                                height={40}
                            />
                        </Box>
                    </Box>
                </Box>

                <Box display="flex" gap={1} sx={{ zIndex: 2, position: 'relative' }}>
                    {!row.is_active ? (
                        <IconButton
                            size="small"
                            onClick={() => undoDelete(row.id)}
                            color="primary"
                            title="Undo Delete"
                            sx={{ backgroundColor: 'white', '&:hover': { backgroundColor: '#f0f0f0' } }}
                        >
                            <UndoIcon />
                        </IconButton>
                    ) : (
                        <IconButton
                            size="small"
                            onClick={() => removeRow(row.id)}
                            color="error"
                            title="Delete"
                        >
                            <DeleteIcon />
                        </IconButton>
                    )}
                </Box>
            </Box>

            {/* Dynamic content area - passed as children */}
            {children}

            {/* Field Creation Modal */}
            <AddFieldModal
                modalVisible={addFieldModalOpen}
                closeModal={(newFieldId?: string) => {
                    setAddFieldModalOpen(false);
                    if (newFieldId) {
                        handleFieldCreated(newFieldId);
                    }
                }}
            />
        </Paper>
    );
};

export default BaseFieldRow;
